import { Translate } from '@teliaee/telia-translate';
import { autorun, observable, runInAction } from 'mobx';
import API from '../util/ApiUtil';
import LanguageStore from './LanguageStore';
import RootStore from './RootStore';

export interface ServiceNumber {
  number: string;
  priceType: 'M' | 'F';
  price?: string | number;
  clearingType?: number;
  serviceProvider: string;
  serviceName: string;
  mobileOpened: boolean;
  fixOpened: boolean;
  roamingOpened: boolean;
  priceDescription?: string[];
  extraInfos?: string[];
}

export interface ServiceNumberList {
  serviceNumbers?: ServiceNumber[];
  status: Status;
}

class BillingStore {
  @observable accessor numberList: ServiceNumberList = { status: 'UNINITIALIZED' };

  private languageStore: LanguageStore;
  private disposeLiveUpdate: () => void;

  public constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
  }

  public subscribe(translate: Translate) {
    if (!this.disposeLiveUpdate) {
      this.disposeLiveUpdate = autorun(() => this.liveUpdate(translate));
    }
  }

  public unsubscribe() {
    if (this.disposeLiveUpdate) {
      this.disposeLiveUpdate();
      delete this.disposeLiveUpdate;
    }
  }

  public async liveUpdate(translate: Translate) {
    await runInAction(async () => await this.fetchServiceNumbers(translate));
  }

  public async fetchServiceNumbers(translate: Translate): Promise<void | ServiceNumberList> {
    runInAction(() => (this.numberList.status = 'FETCHING'));
    return API.get('/billing/servicenumbers').then((response) => {
      runInAction(() => {
        this.numberList = response.data;
        this.applyBusinessLogic(this.numberList, translate);
        this.sortNumbers(this.numberList);
        this.numberList.status = 'FETCHED';
      });
    });
  }

  public applyBusinessLogic(numberList: ServiceNumberList, translate: Translate) {
    Array.from(numberList.serviceNumbers).forEach((serviceNumber) => {
      this.addExtraInfo(serviceNumber, translate);
      this.addPriceDescription(serviceNumber, translate);
      this.removePriceIfNeeded(serviceNumber);
    });
  }

  public addExtraInfo(serviceNumber: ServiceNumber, translate: Translate) {
    serviceNumber.extraInfos = [];

    if (serviceNumber.clearingType === 7) {
      serviceNumber.extraInfos.push(translate('serviceNumber.openInTelia'));
    }
    if (!serviceNumber.mobileOpened) {
      serviceNumber.extraInfos.push(translate('serviceNumber.closedInMobile'));
    }
    if (!serviceNumber.fixOpened) {
      serviceNumber.extraInfos.push(translate('serviceNumber.closedInFix'));
    }
    if (serviceNumber.roamingOpened) {
      serviceNumber.extraInfos.push(translate('serviceNumber.openInRoaming'));
    }
  }

  public addPriceDescription(serviceNumber: ServiceNumber, translate: Translate) {
    serviceNumber.priceDescription = [];
    this.addPriceDescriptionBasedOnClearingType(serviceNumber, translate);
    this.addPriceDescriptionBasedOnPriceType(serviceNumber, translate);
  }

  public addPriceDescriptionBasedOnClearingType(serviceNumber: ServiceNumber, translate: (key: string) => string) {
    if (!this.isPriceBasedClearingType(serviceNumber)) {
      switch (serviceNumber.clearingType) {
        case 1:
          serviceNumber.priceDescription.push(translate('serviceNumber.regularPrice'));
          break;
        case 2:
          serviceNumber.priceDescription.push(translate('serviceNumber.freePrice'));
          break;
        case 7:
          serviceNumber.priceDescription.push(translate('serviceNumber.verifyServicePrice'));
          break;
        default:
      }
    }
  }

  public addPriceDescriptionBasedOnPriceType(serviceNumber: ServiceNumber, translate: (key: string) => string) {
    if (this.isPriceBasedClearingType(serviceNumber) && this.isPriceDisplayingPriceType(serviceNumber)) {
      switch (serviceNumber.priceType) {
        case 'F':
          serviceNumber.priceDescription.push(translate('serviceNumber.eurosPerCall'));
          break;
        case 'M':
          serviceNumber.priceDescription.push(translate('serviceNumber.eurosPerMin'));
          break;
        default:
      }
    }
  }

  public removePriceIfNeeded(serviceNumber: ServiceNumber) {
    if (serviceNumber.clearingType !== 5 && (!this.isPriceBasedClearingType(serviceNumber) || !this.isPriceDisplayingPriceType(serviceNumber))) {
      serviceNumber.price = null;
    }
  }

  public isPriceBasedClearingType(serviceNumber: ServiceNumber) {
    return serviceNumber.clearingType === 3 || serviceNumber.clearingType === 4;
  }

  public isPriceDisplayingPriceType(serviceNumber: ServiceNumber) {
    return serviceNumber.priceType === 'M' || serviceNumber.priceType === 'F';
  }

  public sortNumbers(numberList: ServiceNumberList) {
    numberList.serviceNumbers = numberList.serviceNumbers.sort((a, b) => {
      if (a.number.length > b.number.length) {
        return 1;
      } else if (a.number.length < b.number.length) {
        return -1;
      }
      if (a.number < b.number) {
        return -1;
      } else if (a.number > b.number) {
        return 1;
      }
      return 0;
    });
  }

  public isNumbersFetching() {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.numberList.status);
  }
}

export default BillingStore;
